import dynamic from 'next/dynamic';
import { NspComponentProps } from '../@types/schema';
import { ComponentType, useMemo } from 'react';

/*
The component map translates model keys (e.g. `text_container`) into their dynamic component
counterparts, using the `dynamic()` function of NextJS. Since we do not know at compile
time what components will be ready for a page, we make use of NextJS's dynamic rendering.
*/

// Dynamically loaded components here
const Accordion = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/accordion').then((mod) => mod.Accordion)
);
const BigDataBlock = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/big-data').then((mod) => mod.BigDataBlock)
);
const Buttons = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/components').then((mod) => mod.Buttons)
);
const ButtonCard = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/components').then((mod) => mod.ButtonCard)
);
const CardViewBlock = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/card-view').then((mod) => mod.CardViewBlock)
);
const CardViewContainer = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/card-view').then((mod) => mod.CardViewContainer)
);
const GridBlock = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/grid-container').then((mod) => mod.GridBlock)
);
const GridContainer = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/grid-container').then((mod) => mod.GridContainer)
);
const ImageCard = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/image-card').then((mod) => mod.ImageCard)
);
const MediaBlock = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/components').then((mod) => mod.MediaBlock)
);
const MicrositeFooter = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/components').then((mod) => mod.MicrositeFooter)
);
const PressBar = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/press-bar').then((mod) => mod.PressBar)
);
const PriceCard = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/components').then((mod) => mod.PriceCard)
);
const HtmlContainer = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/html-container').then((mod) => mod.HtmlContainer)
);
const SpotIllustrationBlock = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/spot-illustration').then((mod) => mod.SpotIllustrationBlock)
);
const StructuredText = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/structured-text').then((mod) => mod.StructuredText)
);
const Testimonial = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/testimonial').then((mod) => mod.Testimonial)
);
const TestimonialContainer = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/testimonial').then((mod) => mod.TestimonialContainer)
);
const TextBlock = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/components').then((mod) => mod.TextBlock)
);
const TrustPilotContainer = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/trust-pilot').then((mod) => mod.TrustPilotContainer)
);
const TrustPilotContentBlock = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/trust-pilot').then((mod) => mod.TrustPilotContentBlock)
);
const TwoColumnContainer = dynamic<NspComponentProps>(() =>
  import('@nrdy-marketing-engine/components').then((mod) => mod.TwoColumnContainer)
);

function getComponentMap(): Record<string, ComponentType<NspComponentProps>> {
  return {
    accordion_container: Accordion,
    big_data_block: BigDataBlock,
    button_block: Buttons,
    button_card: ButtonCard,
    card_view_block: CardViewBlock,
    card_view_container: CardViewContainer,
    footer: MicrositeFooter,
    grid_block: GridBlock,
    grid_container: GridContainer,
    html_container: HtmlContainer,
    image_card_content: ImageCard,
    media_block: MediaBlock,
    press_bar: PressBar,
    price_card: PriceCard,
    structured_text_block: StructuredText,
    spot_illustration_block: SpotIllustrationBlock,
    testimonial_container: TestimonialContainer,
    testimonial_content_block: Testimonial,
    text_block: TextBlock,
    text_container: StructuredText,
    trust_pilot_container: TrustPilotContainer,
    trust_pilot_content_block: TrustPilotContentBlock,
    two_column_container: TwoColumnContainer,
  };
}

export const useComponentMap = (component) =>
  useMemo(() => getComponentMap()[component], [component]);

export default getComponentMap;
